import { type ChangeEvent, type KeyboardEvent, memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { PlusIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';

import { useWorkspaceAgentPanelBodyElement } from '~/components/v2/components/workspace/workspace-agent-panel/components/panel/WorkspaceAgentPanelBodyElementContext';
import { workspaceAgentConsentDockShell } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { WorkspaceAgentDockActions } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/consent/WorkspaceAgentDockActions';
import { WorkspaceAgentDockFrame } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/consent/WorkspaceAgentDockFrame';

export type WorkspaceAgentGuidedVariantSizeSavePopoverProps = {
  /** Resolves true when the name was stored. A false result leaves the sheet open. */
  onSave: (name: string) => Promise<boolean>;
  sizeCount: number;
};

export const WorkspaceAgentGuidedVariantSizeSavePopover = memo(
  ({ onSave, sizeCount }: WorkspaceAgentGuidedVariantSizeSavePopoverProps) => {
    const nameId = useId();
    const titleId = useId();
    const nameInputRef = useRef<HTMLInputElement>(null);
    const { element: panelBody } = useWorkspaceAgentPanelBodyElement();
    const [isNaming, setIsNaming] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [name, setName] = useState('');
    const trimmedName = name.trim();
    const isSingleSize = sizeCount === 1;

    const openNaming = useCallback(() => {
      setIsNaming(true);
    }, []);

    const closeNaming = useCallback(() => {
      if (isSaving) {
        return;
      }

      setName('');
      setIsNaming(false);
    }, [isSaving]);

    useEffect(() => {
      if (!isNaming) {
        return;
      }

      nameInputRef.current?.focus({ preventScroll: true });
    }, [isNaming]);

    const handleNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
      setName(event.currentTarget.value);
    }, []);

    const handleSave = useCallback(async () => {
      if (!trimmedName || isSaving) {
        return;
      }

      setIsSaving(true);

      try {
        const didSave = await onSave(trimmedName);

        if (didSave) {
          setName('');
          setIsNaming(false);
        }
      } catch (error) {
        reportErrorToBugsnag({
          context: 'Failed to save workspace agent size selection',
          error,
        });
      } finally {
        setIsSaving(false);
      }
    }, [isSaving, onSave, trimmedName]);

    const handleApprove = useCallback(() => {
      void handleSave();
    }, [handleSave]);

    const handleNameKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeNaming();
          return;
        }

        if (event.key !== 'Enter') {
          return;
        }

        event.preventDefault();
        void handleSave();
      },
      [closeNaming, handleSave],
    );

    const sheet = (
      <section
        aria-labelledby={titleId}
        className={cn(workspaceAgentConsentDockShell(), panelBody && 'absolute inset-x-0 bottom-0 z-10 flex-none')}
        data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SAVE_DOCK"
      >
        <WorkspaceAgentDockFrame
          title={isSingleSize ? 'Name this size' : `Name this set of ${sizeCount} sizes`}
          titleId={titleId}
        >
          <Input
            aria-labelledby={titleId}
            className="w-full rounded-8"
            data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SAVE_NAME"
            id={nameId}
            onChange={handleNameChange}
            onKeyDown={handleNameKeyDown}
            placeholder={isSingleSize ? 'e.g. Retailer square' : 'e.g. Spring launch'}
            ref={nameInputRef}
            size="large"
            value={name}
          />
        </WorkspaceAgentDockFrame>
        <WorkspaceAgentDockActions
          approveLabel="Save"
          isApproveDisabled={!trimmedName || isSaving}
          isApproveLoading={isSaving}
          onApprove={handleApprove}
          onCancel={closeNaming}
        />
      </section>
    );

    const namingSheet = panelBody ? createPortal(sheet, panelBody) : sheet;

    return (
      <>
        <Button
          appearance="ghost"
          color="gray"
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_SMART_RESIZE_SAVE"
          onClick={openNaming}
          prefix={<PlusIcon aria-hidden className="size-3.5" />}
          size="small"
          type="button"
        >
          {isSingleSize ? 'Save size' : 'Save set'}
        </Button>
        {isNaming ? namingSheet : null}
      </>
    );
  },
);

WorkspaceAgentGuidedVariantSizeSavePopover.displayName = 'WorkspaceAgentGuidedVariantSizeSavePopover';
